/**
 *
 * MailConfiguration
 *
 */

import React, { Component } from "react"
import ReactQuill from "react-quill"
import { SketchPicker } from "react-color"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
import { compose } from "redux"

import injectSaga from "utils/injectSaga"
import injectReducer from "utils/injectReducer"
import Loader from "components/Loader"
import { get, pick, omit, isEqual, debounce } from "lodash"
import { Input, Button, InputNumber } from "antd"
import reducer from "./reducer"
import saga from "./saga"
import Preview from "./Preview"
import { getConfigurationWatcher, getImageLogoWatcher } from "./actions"
import {
  makeSelectConfiguration,
  makeSelectIsLoadingConfiguration,
  makeSelectImages,
  makeSelectIsLoadingImages,
  makeSelectLogo,
  makeSelectIsLoadingLogo,
} from "./selectors"

import { resizeImage } from "./resize-image"
import "./index.less"

const Editor = {
  modules: {
    toolbar: [
      [{ header: [1, 2, false] }],
      [
        "bold",
        "italic",
        "underline",
        {
          color: [
            "#000000",
            "#e60000",
            "#ff9900",
            "#ffff00",
            "#008a00",
            "#0066cc",
            "#9933ff",
            "#ffffff",
            "#facccc",
            "#ffebcc",
            "#ffffcc",
            "#cce8cc",
            "#cce0f5",
            "#ebd6ff",
            "#bbbbbb",
            "#f06666",
            "#ffc266",
            "#ffff66",
            "#66b966",
            "#66a3e0",
            "#c285ff",
            "#888888",
            "#a10000",
            "#b26b00",
            "#b2b200",
            "#006100",
            "#0047b2",
            "#6b24b2",
            "#444444",
            "#5c0000",
            "#663d00",
            "#666600",
            "#003700",
            "#002966",
            "#3d1466",
          ],
        },
        { align: ["center", "right", "justify"] },
      ],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
    ],
  },
  formats: [
    "header",
    "bold",
    "italic",
    "underline",
    "list",
    "color",
    "align",
    "bullet",
    "indent",
  ],
}

/* eslint-disable react/prefer-stateless-function */
export class MailConfiguration extends Component {
  reactQuillRef = React.createRef()
  state = {
    isEmptyTextBody: false,
    loadPage: true,
    imageWidth: 0,
    imageHeight: 0,
    email_text: "",
    isOpenSketchPicker: false,
  }

  componentDidMount = () => {
    const search = window.location.search
    const queryParams = new URLSearchParams(search)
    const newStep = queryParams.get("step")

    if (newStep) return

    const {
      match: { params },
    } = this.props

    this.props.getConfigurationWatcher({ idMeasure: params.id })
    this.props.getImageLogoWatcher({ idMeasure: params.id, type: "image" })
    this.props.getImageLogoWatcher({ idMeasure: params.id, type: "logo" })
    setTimeout(() => this.setState({ loadPage: false }), 3000)
  }

  componentDidUpdate = (prevProps) => {
    if (
      !isEqual(prevProps.configuration, this.props.configuration) &&
      this.props.configuration.email_subject
    ) {
      this.setState(
        {
          ...omit(this.props.configuration, ["measure"]),
          isEmptyTextBody: !!this.props.configuration.email_text.replace(
            /(<([^>]+)>)/gi,
            ""
          ),
        },
        () => {
          this.props.setStateFrom("isSavingConfiguration", false)
          this.props.setStateFrom("nameSaving", "Cambios guardados")
        }
      )
    }

    if (!isEqual(prevProps.logo, this.props.logo)) {
      this.debounceSendToServer()
    }

    if (!isEqual(prevProps.images, this.props.images)) {
      if (this.props.images.pathImage && this.props.images.pathImage.img) {
        this.setState({
          imageWidth: this.props.images.pathImage.width,
          imageHeight: this.props.images.pathImage.height,
        })
      } else {
        this.setState({
          imageWidth: this.props.images.pathImageOld.width,
          imageHeight: this.props.images.pathImageOld.height,
        })
      }

      this.debounceSendToServer()
    }
  }

  fileSelectImage = (event) => {
    event.preventDefault()
    const fd = new FormData()
    fd.append("uploaded_file", event.target.files[0])

    const {
      match: { params },
    } = this.props
    const data = {
      idMeasure: params.id,
      type: "image",
      fd,
    }
    this.props.getImageLogoWatcher(data)
    this.fileInputSelectImage.value = ""
  }

  fileSelectLogo = (event) => {
    event.preventDefault()
    const fd = new FormData()

    fd.append("uploaded_file", event.target.files[0])

    const {
      match: { params },
    } = this.props
    const data = {
      idMeasure: params.id,
      type: "logo",
      fd,
    }

    this.props.getImageLogoWatcher(data)
    this.fileInputSelectLogo.value = ""
  }

  setStateFrom = (name, value) => {
    this.setState({ [name]: value })
  }

  sendToServer = () => {
    const {
      logo,
      images,
      match: { params },
    } = this.props

    const data = {
      idMeasure: params.id,
      data: {
        ...omit(this.state, ["loadPage", "imageWidth", "imageHeight"]),
        email_subject: this.state.email_subject,
        logo: logo.pathImage || "",
        image: images.pathImage.img || "",
        dimensions: `${this.state.imageWidth || 0}|${this.state.imageHeight ||
          0}`,
        auto_save: true,
      },
    }

    this.props.getConfigurationWatcher(data)
    this.props.setStateFrom("isSavingConfiguration", true)
    this.validateNextStep()
  }

  validateNextStep = () => {
    const { email_subject, email_text, email_button_text } = this.state

    this.props.setStateFrom(
      "firstStepValidation",
      !email_subject ||
        !email_button_text ||
        (email_text || "").replace(/(<([^>]+)>)/gi, "").length > 500
    )
  }

  debounceSendToServer = debounce(this.sendToServer, 500)

  render() {
    const { loadPage } = this.state

    if (loadPage) return <Loader height="512px" />

    return (
      <div className="measurement-mail-configuration">
        <div className="measurement-mail-configuration-tools">
          <div className="measurement-mail-configuration-affair">
            <span>Asunto del correo</span>
            <Input
              maxLength={40}
              placeholder="Encuesta de clima laboral"
              value={this.state.email_subject}
              disabled={this.props.isLoadingConfiguration}
              onChange={(e) => {
                
                this.setState({ email_subject: e.target.value }, () => {
                  this.debounceSendToServer(); 
                });
              }}
            />
          </div>

          <div className="measurement-mail-configuration-logo">
            <div className="measurement-mail-configuration-logo-header">
              <span>Logo</span>
              <Button
                loading={this.props.isLoadingLogo}
                style={
                  !get(this.props.logo, "pathImage", "")
                    ? {}
                    : { backgroundColor: "#D26C6C", color: "#FFF" }
                }
                onClick={() => {
                  if (!get(this.props.logo, "pathImage", "")) {
                    this.fileInputSelectLogo.click()
                  } else {
                    const fd = new FormData()
                    fd.append("status", "delete")
                    const {
                      match: { params },
                    } = this.props
                    const data = {
                      idMeasure: params.id,
                      type: "logo",
                      fd,
                    }

                    this.props.getImageLogoWatcher(data)
                  }
                }}>
                {!get(this.props.logo, "pathImage", "")
                  ? "Agregar"
                  : "Eliminar"}
              </Button>
              <input
                style={{ display: "none" }}
                type="file"
                accept="image/*"
                onChange={this.fileSelectLogo}
                ref={(fileInputSelectLogo) =>
                  (this.fileInputSelectLogo = fileInputSelectLogo)
                }
              />
            </div>

            {!!get(this.props.logo, "pathImage", "") && (
              <div className="measurement-mail-configuration-logo-body">
                <img src={get(this.props.logo, "pathImage", "")} />
              </div>
            )}
          </div>

          <div className="measurement-mail-configuration-logo">
            <div className="measurement-mail-configuration-logo-header">
              <span>Imagen</span>
              <Button
                loading={this.props.isLoadingImages}
                style={
                  !get(this.props.images, "pathImage.img", "")
                    ? {}
                    : { backgroundColor: "#D26C6C", color: "#FFF" }
                }
                onClick={() => {
                  if (!get(this.props.images, "pathImage.img", "")) {
                    this.fileInputSelectImage.click()
                  } else {
                    const fd = new FormData()
                    fd.append("status", "delete")
                    const {
                      match: { params },
                    } = this.props
                    const data = {
                      idMeasure: params.id,
                      type: "image",
                      fd,
                    }

                    this.props.getImageLogoWatcher(data)
                  }
                }}>
                {!get(this.props.images, "pathImage.img", "")
                  ? "Agregar"
                  : "Eliminar"}
              </Button>
              <input
                style={{ display: "none" }}
                type="file"
                accept="image/*"
                onChange={this.fileSelectImage}
                ref={(fileInputSelectImage) =>
                  (this.fileInputSelectImage = fileInputSelectImage)
                }
              />
            </div>

            {get(this.props.images, "pathImage.img", false) && (
              <div className="measurement-mail-configuration-logo-body">
                <img
                  src={
                    this.props.images
                      ? this.props.images.pathImage.img ||
                        this.props.images.pathImageOld.img
                      : ""
                  }
                  alt="loading..."
                  onLoad={(e) => {
                    if (
                      this.props.images.pathImage &&
                      this.props.images.pathImage.img
                    ) {
                      this.setState({
                        imageWidth: this.props.images.pathImage.width,
                        imageHeight: this.props.images.pathImage.height,
                      })
                    } else {
                      this.setState({
                        imageWidth: this.props.images.pathImageOld.width,
                        imageHeight: this.props.images.pathImageOld.height,
                      })
                    }
                  }}
                />

                <div>
                  <InputNumber
                    min={1}
                    max={700}
                    onChange={(e) => {
                      resizeImage(
                        e,
                        this.state.imageHeight,
                        this.setStateFrom,
                        this.props.images,
                        "width"
                      )
                      this.debounceSendToServer()
                    }}
                    value={this.state.imageWidth}
                  />
                </div>
                <div>
                  <InputNumber
                    min={1}
                    max={700}
                    onChange={(e) => {
                      resizeImage(
                        this.state.imageWidth,
                        e,
                        this.setStateFrom,
                        this.props.images,
                        "height"
                      )
                      this.debounceSendToServer()
                    }}
                    value={this.state.imageHeight}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="measurement-mail-configuration-affair">
            {this.state.email_text.replace(/(<([^>]+)>)/gi, "").length >
              500 && (
              <span
                style={{
                  position: "absolute",
                  right: 20,
                  color: "rgb(210, 108, 108)",
                  fontSize: 12,
                }}>
                Máximo 500 caracteres
              </span>
            )}
            <span>Texto</span>
            <ReactQuill
              ref={this.reactQuillRef}
              modules={Editor.modules}
              formats={Editor.formats}
              className="measurement-mail-configuration-text"
              defaultValue={this.state.email_text}
              // value={this.state.email_text}
              onChange={(e) => {
                this.setState(
                  {
                    email_text: e,
                    isEmptyTextBody:
                      this.reactQuillRef.current !== null &&
                      !!this.reactQuillRef.current
                        .getEditor()
                        .getText()
                        .trim().length,
                  },
                  () => this.debounceSendToServer()
                )
              }}
            />
          </div>
          <div className="measurement-mail-configuration-btn-init">
            <span>Botón de inicio</span>
            <Input
              placeholder="Comenzar"
              maxLength={24}
              value={this.state.email_button_text}
              onChange={(e) => {
                this.setState({ email_button_text: e.target.value }, () =>
                  this.debounceSendToServer()
                )
              }}
              onChange={(e) => {
                this.setState({ email_button_text: e.target.value }, () =>
                  this.debounceSendToServer()
                )
              }}
            />
          </div>
          <div className="measurement-mail-configuration-color-btn-init">
            {this.state.isOpenSketchPicker && (
              <div
                className="measurement-mail-configuration-colorpicker"
                onMouseLeave={() =>
                  this.setStateFrom("isOpenSketchPicker", false)
                }>
                <SketchPicker
                  color={this.state.email_background_button}
                  onChangeComplete={({ hex }) => {
                    this.setState({ email_background_button: hex }, () =>
                      this.debounceSendToServer()
                    )
                  }}
                />
              </div>
            )}
            <span>Color botón de inicio</span>
            <div
              style={{ backgroundColor: this.state.email_background_button }}
              onClick={() => {
                this.setStateFrom(
                  "isOpenSketchPicker",
                  !this.state.isOpenSketchPicker
                )
              }}
            />
          </div>
        </div>

        <div style={{ margin: "24px 30px" }}>
          <Preview
            imageUrl={this.props.images}
            logoUrl={this.props.logo}
            {...pick(this.props, ["isLoadingImageUrl"])}
            {...pick(this.state, [
              "email_button_text",
              "email_text",
              "imageWidth",
              "imageHeight",
              "isEmptyTextBody",
              "email_background_button",
            ])}
          />
        </div>
      </div>
    )
  }
}

MailConfiguration.propTypes = {
  // dispatch: PropTypes.func.isRequired,
}

const mapStateToProps = createStructuredSelector({
  configuration: makeSelectConfiguration(),
  isLoadingConfiguration: makeSelectIsLoadingConfiguration(),
  images: makeSelectImages(),
  isLoadingImages: makeSelectIsLoadingImages(),
  logo: makeSelectLogo(),
  isLoadingLogo: makeSelectIsLoadingLogo(),
})

function mapDispatchToProps(dispatch) {
  return {
    getConfigurationWatcher: (params) =>
      dispatch(getConfigurationWatcher(params)),
    getImageLogoWatcher: (params) => dispatch(getImageLogoWatcher(params)),
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)

const withReducer = injectReducer({ key: "mailConfiguration", reducer })
const withSaga = injectSaga({ key: "mailConfiguration", saga })

export default compose(withReducer, withSaga, withConnect)(MailConfiguration)
